/**
 * The arena features on screen (Phase 8): pickups that bob and vanish while they respawn,
 * boost pads and hazards as emissive floor marks, moving platforms posed from the same tick
 * function the rules use, and concealment zones as translucent volumes. Everything reads the
 * sidecar and the driver's state; nothing here decides anything.
 */
import {
  platformPoseAt,
  type ArenaColliders,
  type ArenaRuntime,
  type PlatformPose,
} from '@steelwake/shared/arena';
import { SIM_TICK_RATE } from '@steelwake/shared';
import {
  BoxGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicNodeMaterial,
  MeshStandardNodeMaterial,
  NormalBlending,
  OctahedronGeometry,
  PlaneGeometry,
  type Object3D,
  type Scene,
} from 'three/webgpu';
import { color, mrt, uniform, vec2, vec3, vec4 } from 'three/tsl';

import { disposeObject } from '../assets/dispose.js';
import { createVfxMaterial, type VfxMaterial } from './vfx/material.js';

const PICKUP_COLORS = { shieldCell: 0x6fa8ff, heatSink: 0x7ed957 } as const;
const PAD_COLOR = 0x5cf2ff;
const HAZARD_COLOR = 0xff5a1a;
const STEAM_COLOR = 0x9fb4c2;
/** A taken pickup grows back over the last half second before it is available again. */
const RESPAWN_GROW_TICKS = SIM_TICK_RATE / 2;

interface PickupView {
  mesh: Mesh;
  baseY: number;
  phase: number;
}

interface PlatformView {
  node: Object3D;
  start: PlatformPose;
  pose: PlatformPose;
}

/** A soft, normal-blended volume that stays out of the G-buffer like every effect. */
function volumeMaterial(hex: number, opacity: number): MeshBasicNodeMaterial {
  const material = new MeshBasicNodeMaterial();
  material.colorNode = color(hex);
  material.opacityNode = uniform(opacity);
  material.transparent = true;
  material.blending = NormalBlending;
  material.depthWrite = false;
  material.side = DoubleSide;
  material.mrtNode = mrt({ normal: vec3(0), velocity: vec2(0), diffuseColor: vec4(0) });
  return material;
}

export class ArenaFeatureViews {
  private readonly group = new Group();
  private readonly pickups: PickupView[] = [];
  private readonly platforms: PlatformView[] = [];
  private readonly hazards: VfxMaterial[] = [];
  private readonly materials: { dispose(): void }[] = [];
  private readonly fallbackPlatforms: Mesh[] = [];

  constructor(
    private readonly scene: Scene,
    private readonly arena: ArenaColliders,
    root: Object3D,
  ) {
    this.group.name = 'arena-features';
    scene.add(this.group);

    for (const pickup of arena.pickups) {
      const vfx = createVfxMaterial(PICKUP_COLORS[pickup.kind], 2.2);
      this.materials.push(vfx.material);
      const mesh = new Mesh(new OctahedronGeometry(0.55, 0), vfx.material);
      mesh.position.set(pickup.position[0], pickup.position[1] + 1.2, pickup.position[2]);
      this.group.add(mesh);
      this.pickups.push({ mesh, baseY: pickup.position[1] + 1.2, phase: this.pickups.length });
    }

    for (const pad of arena.boostPads) {
      const vfx = createVfxMaterial(PAD_COLOR, 1.6);
      this.materials.push(vfx.material);
      const mesh = new Mesh(
        new PlaneGeometry(pad.box.halfExtents[0] * 2, pad.box.halfExtents[2] * 2),
        vfx.material,
      );
      mesh.rotation.x = -Math.PI / 2;
      mesh.rotation.z = pad.box.yaw;
      mesh.position.set(
        pad.box.center[0],
        pad.box.center[1] - pad.box.halfExtents[1] + 0.1,
        pad.box.center[2],
      );
      this.group.add(mesh);
    }

    for (const hazard of arena.hazards) {
      const vfx = createVfxMaterial(HAZARD_COLOR, 1.2);
      this.materials.push(vfx.material);
      this.hazards.push(vfx);
      const mesh = new Mesh(
        new PlaneGeometry(hazard.box.halfExtents[0] * 2, hazard.box.halfExtents[2] * 2),
        vfx.material,
      );
      mesh.rotation.x = -Math.PI / 2;
      mesh.rotation.z = hazard.box.yaw;
      mesh.position.set(
        hazard.box.center[0],
        hazard.box.center[1] - hazard.box.halfExtents[1] + 0.12,
        hazard.box.center[2],
      );
      this.group.add(mesh);
    }

    for (const zone of arena.concealment) {
      const material = volumeMaterial(STEAM_COLOR, 0.32);
      this.materials.push(material);
      const mesh = new Mesh(
        new BoxGeometry(
          zone.box.halfExtents[0] * 2,
          zone.box.halfExtents[1] * 2,
          zone.box.halfExtents[2] * 2,
        ),
        material,
      );
      mesh.position.set(zone.box.center[0], zone.box.center[1], zone.box.center[2]);
      mesh.rotation.y = zone.box.yaw;
      this.group.add(mesh);
    }

    for (const platform of arena.platforms) {
      const start = platformPoseAt(platform, 0);
      let node = root.getObjectByName(`PLATFORM_${platform.id}`) ?? null;
      if (!node) {
        // The pipeline names every platform; a missing node still gets a plain box so the
        // rules and the picture never disagree.
        const material = new MeshStandardNodeMaterial({ color: 0x556070 });
        this.materials.push(material);
        const mesh = new Mesh(
          new BoxGeometry(
            platform.halfExtents[0] * 2,
            platform.halfExtents[1] * 2,
            platform.halfExtents[2] * 2,
          ),
          material,
        );
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.position.set(start.center.x, start.center.y, start.center.z);
        mesh.rotation.y = start.yaw;
        this.group.add(mesh);
        this.fallbackPlatforms.push(mesh);
        node = mesh;
      }
      this.platforms.push({
        node,
        start,
        pose: { center: { x: 0, y: 0, z: 0 }, yaw: 0 },
      });
    }
  }

  /**
   * `viewTick` is the driver's own (fractional) timeline; `time` drives the idle motion. The
   * pickup runtime is index-aligned with the sidecar's pickup list.
   */
  update(runtime: ArenaRuntime, viewTick: number, time: number): void {
    for (let i = 0; i < this.pickups.length; i++) {
      const view = this.pickups[i];
      const pickup = this.arena.pickups[i];
      const state = runtime.pickups[i];
      if (!view || !pickup) continue;
      const respawnTick = state?.respawnTick ?? 0;
      const left = respawnTick - viewTick;
      const scale =
        left <= 0 ? 1 : left >= RESPAWN_GROW_TICKS ? 0.02 : 1 - left / RESPAWN_GROW_TICKS;
      view.mesh.scale.setScalar(scale);
      view.mesh.position.y = view.baseY + Math.sin(time * 2 + view.phase) * 0.2;
      view.mesh.rotation.y = time * 1.2 + view.phase;
    }
    for (const hazard of this.hazards) {
      hazard.intensity.value = 1.2 + Math.sin(time * 3) * 0.35;
    }
    for (let i = 0; i < this.platforms.length; i++) {
      const view = this.platforms[i];
      const platform = this.arena.platforms[i];
      if (!view || !platform) continue;
      // The mesh was baked at the start pose (tick 0); move it by the pose delta since then.
      const pose = platformPoseAt(platform, viewTick, view.pose);
      view.node.position.set(
        pose.center.x - view.start.center.x,
        pose.center.y - view.start.center.y,
        pose.center.z - view.start.center.z,
      );
    }
  }

  /** How many platforms are drawn from the sidecar's own nodes (tests and the debug API). */
  count(): { pickups: number; platforms: number; fallbackPlatforms: number } {
    return {
      pickups: this.pickups.length,
      platforms: this.platforms.length,
      fallbackPlatforms: this.fallbackPlatforms.length,
    };
  }

  dispose(): void {
    this.scene.remove(this.group);
    disposeObject(this.group);
    for (const m of this.materials) m.dispose();
  }
}
